import React, { useEffect } from "react";
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { ToastContainer, toast } from 'react-toastify';
import Layout from '../components/Layout';
import axios from 'axios';
import HeadMeta from '../components/HeadMeta';
import LocalizedLink from '../components/LocalizedLink';
import legalLinks from '../data/legalLinks';

const resetForm = () => {
  document.getElementById('contact-form').reset();
};

const metaData = {
  en: {
    title: `Contact — TriDyme`,
    pageTitle: "Contact Us",
    description: "A question about our solutions? Need a personalized demo? Our team is here to help."
  },
  fr: {
    title: `Contact — TriDyme`,
    pageTitle: "Contactez-nous",
    description: "Une question sur nos solutions ? Besoin d'une démo personnalisée ? Notre équipe est à votre écoute."
  }
};

const gdprTexts = {
  fr: {
    marketing: 'J’accepte de recevoir des informations commerciales de TriDyme (actualités, invitations). Facultatif, désinscription possible à tout moment.',
    notice: 'Les informations saisies sont traitées par TriAzur SAS (TriDyme) pour répondre à votre demande et, le cas échéant, assurer le suivi de la relation commerciale. Elles sont conservées 3 ans après notre dernier échange. Vous pouvez exercer vos droits d’accès, de rectification, d’effacement et d’opposition à contact@tridyme.com.',
    more: 'Politique de confidentialité',
  },
  en: {
    marketing: 'I agree to receive commercial information from TriDyme (news, invitations). Optional, you can unsubscribe at any time.',
    notice: 'The information you enter is processed by TriAzur SAS (TriDyme) to answer your request and, where applicable, to follow up on the business relationship. It is kept for 3 years after our last exchange. You can exercise your rights of access, rectification, erasure and objection at contact@tridyme.com.',
    more: 'Privacy policy',
  },
};

const SUBJECTS = ['telecom', 'enterprise', 'demo', 'pricing', 'partnership', 'developer', 'support', 'other'];

const ContactContent = injectIntl(({ locale, intl }) => {
  // Pre-select the subject from the URL, e.g. /contact/?subject=enterprise
  useEffect(() => {
    const subject = new URLSearchParams(window.location.search).get('subject');
    if (subject && SUBJECTS.includes(subject)) {
      document.getElementById('subject').value = subject;
    }
  }, []);

  const gotEmail = async (e) => {
    e.preventDefault();
    await axios.post(`https://platform.tridyme.com/api/contact`, {
      firstname: document.getElementById("firstname").value,
      lastname: document.getElementById("lastname").value,
      company: document.getElementById("company").value,
      email: document.getElementById("email").value,
      phone: document.getElementById("phone").value,
      subject: document.getElementById("subject").value,
      message: document.getElementById("message").value,
      // TODO: check that the platform API stores this optional consent (and its date)
      marketingConsent: document.getElementById("marketingConsent").checked
    }).then(function (response) {
      console.log(response);
      resetForm();
      toast.success(intl.formatMessage({ id: 'contact.toast.success' }), {
        autoClose: 1500
      });
    })
      .catch(function (error) {
        console.log(error);
        toast.error(intl.formatMessage({ id: 'contact.toast.error' }), {
          autoClose: 1500
        });
      });
  };

  return (
    <>
      <ToastContainer />

      <div className="contact-page">
        {/* Page Header */}
        <section className="contact-page-header">
          <h1>{metaData[locale].pageTitle}</h1>
          <p>{metaData[locale].description}</p>
        </section>

        {/* Contact Section */}
        <section className="contact-section">
          <div className="contact-container">
            {/* Contact Info */}
            <div className="contact-info">
              <h2>
                <FormattedMessage
                  id="contact.talkAboutProject"
                  defaultMessage="Parlons de votre projet"
                />
              </h2>

              <div className="contact-cards">
                <div className="contact-card">
                  <div className="contact-card-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                    </svg>
                  </div>
                  <div className="contact-card-content">
                    <h3>
                      <FormattedMessage id="contact.phone" defaultMessage="Téléphone" />
                    </h3>
                    <p><a href="tel:+33785175050">+33 7 85 17 50 50</a></p>
                  </div>
                </div>

                <div className="contact-card">
                  <div className="contact-card-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <path d="M22 6l-10 7L2 6" />
                    </svg>
                  </div>
                  <div className="contact-card-content">
                    <h3>Email</h3>
                    <p><a href="mailto:contact@tridyme.com">contact@tridyme.com</a></p>
                  </div>
                </div>

                <div className="contact-card">
                  <div className="contact-card-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div className="contact-card-content">
                    <h3>
                      <FormattedMessage id="contact.address" defaultMessage="Adresse" />
                    </h3>
                    <p>18 Rue d'Arras<br />92000 Nanterre, France</p>
                  </div>
                </div>
              </div>


              {/* Team Section */}
              <div className="team-section">
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <h2>
                <FormattedMessage
                  id="contact.sendMessage"
                  defaultMessage="Envoyez-nous un message"
                />
              </h2>
              <p>
                <FormattedMessage
                  id="contact.formIntro"
                  defaultMessage="Remplissez le formulaire ci-dessous et nous vous répondrons sous 24h."
                />
              </p>

              <form className="contact-form" id="contact-form" onSubmit={gotEmail} method="post">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstname">
                      <FormattedMessage id="contact.firstname" defaultMessage="Prénom" /> <span className="required">*</span>
                    </label>
                    <input type="text" id="firstname" name="firstname" required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastname">
                      <FormattedMessage id="contact.lastname" defaultMessage="Nom" /> <span className="required">*</span>
                    </label>
                    <input type="text" id="lastname" name="lastname" required />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email <span className="required">*</span></label>
                    <input type="email" id="email" name="email" required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">
                      <FormattedMessage id="contact.phone" defaultMessage="Téléphone" />
                    </label>
                    <input type="tel" id="phone" name="phone" />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="company">
                    <FormattedMessage id="contact.company" defaultMessage="Entreprise" />
                  </label>
                  <input type="text" id="company" name="company" />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">
                    <FormattedMessage id="contact.subject" defaultMessage="Sujet" /> <span className="required">*</span>
                  </label>
                  <select id="subject" name="subject" required>
                    <option value="">
                      {intl.formatMessage({ id: 'contact.subject.select' })}
                    </option>
                    {SUBJECTS.map((subject) => (
                      <option key={subject} value={subject}>
                        {intl.formatMessage({ id: `contact.subject.${subject}` })}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message <span className="required">*</span></label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder={intl.formatMessage({ id: 'contact.message.placeholder' })}
                    required
                  ></textarea>
                </div>

                <div className="form-group form-consent">
                  <label htmlFor="marketingConsent" className="form-consent-label">
                    <input type="checkbox" id="marketingConsent" name="marketingConsent" />
                    <span>{gdprTexts[locale].marketing}</span>
                  </label>
                </div>

                <p className="form-gdpr-notice">
                  {gdprTexts[locale].notice}{' '}
                  <LocalizedLink to={legalLinks[locale].privacy}>{gdprTexts[locale].more}</LocalizedLink>
                </p>

                <div className="form-submit">
                  <button type="submit" className="btn btn-blue btn-large">
                    <FormattedMessage id="contact.send" defaultMessage="Envoyer le message" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>

      </div>
    </>
  );
});

const Contact = ({ pageContext: { locale } }) => {
  return (
    <Layout locale={locale}>
      <HeadMeta locale={locale} metaData={metaData} />
      <ContactContent locale={locale} />
    </Layout>
  );
};

Contact.propTypes = {
  pageContext: PropTypes.shape({
    locale: PropTypes.string.isRequired
  }).isRequired
};

export default Contact;
